/**
 * Free trial on GET /vendor/subscription → current_subscription.is_trial.
 * Paid subscriptions use false, 0, or omit the field, so their dates stay visible.
 */
export function isFreeTrialSubscription(subscription) {
  const value = subscription?.is_trial;
  if (value === true || value === 1 || value === '1') return true;
  if (typeof value === 'string' && value.trim().toLowerCase() === 'true') return true;
  return false;
}

const TRIAL_ACTIVATION_POPUP_KEY = 'trial-activation-popup';
export const TRIAL_ACTIVATION_POPUP_EVENT = 'trial-activation-popup';

const notifyTrialActivationPopupChanged = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(TRIAL_ACTIVATION_POPUP_EVENT));
};

const pickSubscriptionDate = (...values) =>
  values.find((value) => value != null && String(value).trim() !== '') ?? null;

export function getSubscriptionStartDate(subscription) {
  return pickSubscriptionDate(
    subscription?.start_date,
    subscription?.started_at,
    subscription?.starts_at,
  );
}

export function getSubscriptionEndDate(subscription) {
  return pickSubscriptionDate(
    subscription?.expiry_date,
    subscription?.end_date,
    subscription?.expires_at,
    subscription?.expired_at,
  );
}

/**
 * Vendor dashboard banner source.
 * GET /vendor/subscription may return current_subscription: null after expiry
 * and keep the last plan only in history (store 138).
 */
export function resolveVendorBannerSubscription(subscriptionResponse, store) {
  const payload = subscriptionResponse?.data ?? {};
  const current = payload.current_subscription;
  if (current && typeof current === 'object') {
    return { record: current, forceInactive: false };
  }

  const history = Array.isArray(payload.history)
    ? payload.history.filter((item) => item && typeof item === 'object')
    : [];
  if (history.length) {
    return { record: history[0], forceInactive: true };
  }

  const storeCurrent = store?.currentSubscription || store?.current_subscription;
  if (storeCurrent && typeof storeCurrent === 'object') {
    return { record: storeCurrent, forceInactive: false };
  }

  return { record: null, forceInactive: false };
}

export function saveTrialActivationPopup(subscription) {
  if (!isFreeTrialSubscription(subscription)) return;
  try {
    sessionStorage.setItem(
      TRIAL_ACTIVATION_POPUP_KEY,
      JSON.stringify({
        startDate: getSubscriptionStartDate(subscription),
        endDate: getSubscriptionEndDate(subscription),
      }),
    );
    notifyTrialActivationPopupChanged();
  } catch (_) {
    /* ignore */
  }
}

export function peekTrialActivationPopup() {
  try {
    const raw = sessionStorage.getItem(TRIAL_ACTIVATION_POPUP_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

export function clearTrialActivationPopup() {
  try {
    sessionStorage.removeItem(TRIAL_ACTIVATION_POPUP_KEY);
    notifyTrialActivationPopupChanged();
  } catch (_) {
    /* ignore */
  }
}

/**
 * The global Notification Popup also receives the backend's subscription-activation
 * push. While the trial success Dialog is pending, that push is the same event —
 * not a separate system message.
 */
function isSubscriptionActivationNotice(title, body, payload) {
  const type = String(
    payload?.data?.type
    || payload?.data?.notification_type
    || payload?.data?.event
    || '',
  );
  const text = `${title || ''} ${body || ''} ${type}`.toLowerCase();
  return (
    (/اشتراك/.test(text) && /تفعيل/.test(text))
    || (/subscription/.test(text) && /activat/.test(text))
    || (/trial/.test(text) && /activat/.test(text))
    || /فترة تجريبية/.test(text)
  );
}

export function shouldSuppressSubscriptionActivationNotice(title, body, payload) {
  if (!isSubscriptionActivationNotice(title, body, payload)) return false;
  if (peekTrialActivationPopup()) return true;
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  return path === '/subscription-packages' || path === '/continue-to-settings';
}
